import { createSelector } from '@reduxjs/toolkit';
import { contains, filter, get, getOr, map, mapValues } from 'lodash/fp';
import type { RootState } from 'app/store/reducers';
import { UNHEALTHY_TYPE } from 'app/store/__generated__/types';
import {
    NOT_AUTHENTICATED_TO_SNOWFLAKE,
    SNOWFLAKE_NOT_CONFIGURED,
    UNCATEGORIZED_SQL_ERROR
} from 'app/store/__generated__/errors';
import { SnowflakeConfigurationType, SnowflakeConnectionState } from 'app/lib/types/server';
import { Defaults, SANDBOX_ACCOUNT_NAME } from 'app/lib/constants';
import { selectUserCanManageSnowflake } from 'app/store/users';
import type { SnowflakeConnectionHealthcheck } from './types';

const selectSlice = (state: RootState) => state.healthcheck;
const selectApplicationStatus = createSelector([selectSlice], ({ serviceByName }) => serviceByName);

export const selectDegradedSystems = createSelector(
    selectApplicationStatus,
    filter({ _type: UNHEALTHY_TYPE })
);
const selectDegradedSystemNames = createSelector([selectDegradedSystems], map('name'));

export const selectIsInMaintenance = createSelector(
    [selectDegradedSystemNames],
    contains('maintenance-mode-health-check')
);

export const selectIsSuspended = createSelector(
    [selectDegradedSystemNames],
    contains('instance-active-health-check')
);

export const selectSnowflakeConnection = createSelector(selectSlice, get('snowflakeConnection'));

export const selectCurrentRole = createSelector(selectSnowflakeConnection, get('role'));
export const selectCurrentWarehouse = createSelector(selectSnowflakeConnection, get('warehouse'));

const selectSnowflakeConnectionType = createSelector(selectSnowflakeConnection, get('type'));
export const selectSnowflakeConnectionIsOauth = createSelector(
    selectSnowflakeConnectionType,
    (type) => type === SnowflakeConfigurationType.EXTERNAL_OAUTH
);

export const selectIsSandboxedSystem = createSelector(
    [selectSnowflakeConnection],
    (connection) => connection?.account?.toLowerCase() === SANDBOX_ACCOUNT_NAME
);

export const selectIsSnowflakeConnected = createSelector(
    selectSnowflakeConnection,
    (connection) => connection?.state === SnowflakeConnectionState.CONNECTED
);

function isSnowflakeErrorReason(reason: SnowflakeConnectionHealthcheck['reason'], kind: string) {
    if (!reason) {
        return false;
    }
    return reason._kind === kind;
}

export const selectMappedSnowflakeStatus = createSelector(
    selectSnowflakeConnection,
    selectUserCanManageSnowflake,
    (snowflakeConnection, canManageSnowflake) => {
        if (!snowflakeConnection) {
            return { noStatus: true };
        }
        const { state, warehouse, reason } = snowflakeConnection;

        return {
            isConnected: state === SnowflakeConnectionState.CONNECTED,
            isConnectedWithWarning: !warehouse && state === SnowflakeConnectionState.CONNECTED,
            isUnconfigured: isSnowflakeErrorReason(reason, SNOWFLAKE_NOT_CONFIGURED),
            isUnconfiguredAdmin:
                canManageSnowflake && isSnowflakeErrorReason(reason, SNOWFLAKE_NOT_CONFIGURED),
            isSignInRequired:
                isSnowflakeErrorReason(reason, NOT_AUTHENTICATED_TO_SNOWFLAKE) ||
                isSnowflakeErrorReason(reason, UNCATEGORIZED_SQL_ERROR),
            isError:
                reason !== null &&
                !isSnowflakeErrorReason(reason, SNOWFLAKE_NOT_CONFIGURED) &&
                !isSnowflakeErrorReason(reason, NOT_AUTHENTICATED_TO_SNOWFLAKE)
        };
    }
);

export const selectWarehouseStatus = createSelector(
    selectSnowflakeConnection,
    (snowflakeConnection) => snowflakeConnection?.warehouseStatus
);

export const selectIsWarehouseLoadDialogOpen = createSelector(
    selectSlice,
    get('loadDialog.isOpen')
);

export const selectWasWarehouseLoadDialogOpenedAutomatically = createSelector(
    selectSlice,
    get('loadDialog.wasOpenedAutomatically')
);

const selectWarehouseStatusByName = createSelector(
    selectSlice,
    (slice) => slice.warehouseStatusByName
);

export const selectCurrentWarehouseStatus = createSelector(
    selectCurrentWarehouse,
    selectWarehouseStatusByName,
    getOr(Defaults.OBJECT)
);

export const selectWarehouseStatuses = createSelector(
    selectWarehouseStatusByName,
    (warehousesByName) =>
        mapValues(
            (wh) => ({
                name: wh.name,
                size: wh.size,
                state: wh.isOverloaded ? 'OVERLOADED' : wh.state,
                running: wh.samples.at(-1)?.running,
                queued: wh.samples.at(-1)?.queued
            }),
            warehousesByName
        )
);
